import React, { useState } from 'react';

/**
 * OptimizedImage component that handles image optimization, loading, and errors
 * 
 * @param {string} src - The source URL of the image
 * @param {string} alt - Alt text for the image
 * @param {string} className - Additional CSS classes
 * @param {string} objectFit - CSS object-fit property (cover, contain, etc.)
 * @param {number} aspectRatio - Force specific aspect ratio (optional)
 * @param {string} fallbackSrc - Fallback image source if main image fails to load
 * @param {object} props - Any additional props to pass to the img element
 */
const OptimizedImage = ({
  src,
  alt,
  className = '',
  objectFit = 'cover',
  aspectRatio,
  fallbackSrc = 'https://via.placeholder.com/200x150?text=Image+Not+Found',
  ...props
}) => {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleError = () => {
    setError(true);
    setLoaded(true);
  };

  const handleLoad = () => {
    setLoaded(true);
  };

  const containerStyle = aspectRatio ? { 
    paddingBottom: `${(1 / aspectRatio) * 100}%`,
    position: 'relative'
  } : {};

  const imageStyle = aspectRatio ? {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit
  } : { objectFit };

  return (
    <div className={`image-container ${aspectRatio ? 'relative' : ''}`} style={containerStyle}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <img
        src={error ? fallbackSrc : src}
        alt={alt}
        className={`transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
        style={imageStyle}
        onError={handleError}
        onLoad={handleLoad}
        loading="lazy"
        {...props}
      />
    </div>
  );
};

export default OptimizedImage; 